import { useEffect, useState } from 'react';
import { calendarApi, type CalendarData } from './api';

/** Kalenderdata, gedeeld door de kalenderpagina en widgets (10 minuten bewaard). */

let cached: { at: number; p: Promise<CalendarData> } | null = null;

export function loadCalendar(force = false): Promise<CalendarData> {
  if (!cached || force || Date.now() - cached.at > 10 * 60_000) {
    const p = calendarApi.get(30, 7);
    cached = { at: Date.now(), p };
    p.catch(() => {
      cached = null;
    });
  }
  return cached.p;
}

export function useCalendar(): { data: CalendarData | null; error: string | null } {
  const [data, setData] = useState<CalendarData | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadCalendar()
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Laden mislukt'));
    return () => {
      live = false;
    };
  }, []);
  return { data, error };
}

/** Vandaag als YYYY-MM-DD in lokale tijd. */
export function todayYmd(offsetDays = 0): string {
  const d = new Date(Date.now() + offsetDays * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** "Vandaag", "Morgen", "Gisteren", "do 29 okt" (kort) of "donderdag 29 oktober" (lang). */
export function dayName(ymd: string, long = false): string {
  if (ymd === todayYmd()) return 'Vandaag';
  if (ymd === todayYmd(1)) return 'Morgen';
  if (ymd === todayYmd(-1)) return 'Gisteren';
  const d = parseYmd(ymd);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('nl-NL', long
    ? { weekday: 'long', day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }) }
    : { weekday: 'short', day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

export function fullDate(ymd: string): string {
  return parseYmd(ymd).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
}

export const RELEASE_LABEL = { theatrical: 'In de bioscoop', digital: 'Digitaal', physical: 'Op dvd/blu-ray' } as const;
